import * as types from '../mutation-types';
import ContactAPI from '../../api/contacts';
import ConversationApi from '../../api/conversations';
import camelcaseKeys from 'camelcase-keys';

export const createMessagePayload = (payload, message) => {
  const { content, cc_emails, bcc_emails } = message;
  payload.append('message[content]', content);
  if (cc_emails) payload.append('message[cc_emails]', cc_emails);
  if (bcc_emails) payload.append('message[bcc_emails]', bcc_emails);
};

export const createConversationPayload = ({ params, contactId, files }) => {
  const { inboxId, message, sourceId, mailSubject, assigneeId } = params;
  const payload = new FormData();

  if (message) {
    createMessagePayload(payload, message);
  }

  if (files && files.length > 0) {
    files.forEach(file => payload.append('message[attachments][]', file));
  }

  payload.append('inbox_id', inboxId);
  payload.append('contact_id', contactId);
  payload.append('source_id', sourceId);
  if (mailSubject) {
    payload.append('additional_attributes[mail_subject]', mailSubject);
  }
  payload.append('assignee_id', assigneeId);

  return payload;
};

export const createWhatsAppConversationPayload = ({ params }) => {
  const { inboxId, message, contactId, sourceId, assigneeId } = params;

  const payload = {
    inbox_id: inboxId,
    contact_id: contactId,
    source_id: sourceId,
    message,
    assignee_id: assigneeId,
  };

  return payload;
};

const setNewConversationPayload = ({
  isFromWhatsApp,
  params,
  contactId,
  files,
}) => {
  if (isFromWhatsApp) {
    return createWhatsAppConversationPayload({ params });
  }
  return createConversationPayload({
    params,
    contactId,
    files,
  });
};

const state = {
  records: {},
  pagination: {},
  uiFlags: {
    isFetching: false,
    isLoadingMore: false,
  },
};

// ponytail: one recent contact is enough to stop remount bursts; expand only if parallel contacts repeat.
let recentGet;
let accessGeneration = 0;

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getContactConversation: $state => id => {
    return $state.records[Number(id)] || [];
  },
  getAllConversationsByContactId: $state => id => {
    const records = $state.records[Number(id)] || [];
    return camelcaseKeys(records, { deep: true });
  },
  getHasMore: $state => id => Boolean($state.pagination[Number(id)]),
};

export const actions = {
  clear: ({ commit, state: $state }) => {
    accessGeneration += 1;
    recentGet = undefined;
    Object.keys($state.records).forEach(id =>
      commit(types.default.DELETE_CONTACT_CONVERSATION, id)
    );
  },
  create: async ({ commit }, { params, isFromWhatsApp }) => {
    const requestGeneration = accessGeneration;
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isCreating: true,
    });
    const { contactId, files } = params;
    try {
      const payload = setNewConversationPayload({
        isFromWhatsApp,
        params,
        contactId,
        files,
      });

      const { data } = await ConversationApi.create(payload);
      if (requestGeneration === accessGeneration) {
        commit(types.default.ADD_CONTACT_CONVERSATION, {
          id: contactId,
          data,
        });
      }
      recentGet = undefined;

      return data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isCreating: false,
      });
    }
  },
  get: async ({ commit, rootGetters }, contactId) => {
    const requestGeneration = accessGeneration;
    const userId = rootGetters?.getCurrentUserID;
    const key = userId && `${userId}:${ContactAPI.url}/${contactId}`;
    if (key && recentGet?.key === key) {
      if (recentGet.promise) {
        await recentGet.promise.catch(() => {});
        return;
      }
      if (Date.now() - recentGet.at < 10000) return;
    }

    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isFetching: true,
    });
    try {
      const request = ContactAPI.getConversations(contactId);
      if (key) recentGet = { key, promise: request };
      const response = await request;
      if (requestGeneration !== accessGeneration) return;
      commit(types.default.SET_CONTACT_CONVERSATIONS, {
        id: contactId,
        data: response.data.payload,
        nextCursor: response.data.meta?.next_cursor,
      });
      if (key && recentGet?.key === key) recentGet = { key, at: Date.now() };
    } catch (error) {
      if (requestGeneration !== accessGeneration) return;
      if (key && recentGet?.key === key) recentGet = undefined;
      if ([401, 403, 404].includes(error.response?.status)) {
        commit(types.default.SET_CONTACT_CONVERSATIONS, {
          id: contactId,
          data: [],
          nextCursor: null,
        });
      }
    } finally {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  loadMore: async ({ commit, state: $state }, contactId) => {
    const requestGeneration = accessGeneration;
    const cursor = $state.pagination[Number(contactId)];
    if (!cursor || $state.uiFlags.isLoadingMore) return;

    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isLoadingMore: true,
    });
    try {
      const response = await ContactAPI.getConversations(contactId, {
        cursor,
      });
      if (requestGeneration !== accessGeneration) return;
      commit(types.default.SET_CONTACT_CONVERSATIONS, {
        id: contactId,
        data: response.data.payload,
        nextCursor: response.data.meta?.next_cursor,
        append: true,
      });
    } catch (error) {
      if (requestGeneration !== accessGeneration) return;
      if ([401, 403, 404].includes(error.response?.status)) {
        commit(types.default.SET_CONTACT_CONVERSATIONS, {
          id: contactId,
          data: [],
          nextCursor: null,
        });
      }
    } finally {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isLoadingMore: false,
      });
    }
  },
};

export const mutations = {
  [types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_CONTACT_CONVERSATIONS]: (
    $state,
    { id, data, nextCursor, append = false }
  ) => {
    const records = append ? $state.records[id] || [] : [];
    const seen = new Set(records.map(conversation => conversation.id));
    $state.records = {
      ...$state.records,
      [id]: [
        ...records,
        ...data.filter(conversation => !seen.has(conversation.id)),
      ],
    };
    $state.pagination = {
      ...$state.pagination,
      [id]: nextCursor || null,
    };
  },
  [types.default.ADD_CONTACT_CONVERSATION]: ($state, { id, data }) => {
    const conversations = $state.records[id] || [];

    const updatedConversations = [...conversations];
    const index = conversations.findIndex(
      conversation => conversation.id === data.id
    );

    if (index !== -1) {
      updatedConversations[index] = { ...conversations[index], ...data };
    } else {
      updatedConversations.push(data);
    }

    $state.records = {
      ...$state.records,
      [id]: updatedConversations,
    };
  },
  [types.default.DELETE_CONTACT_CONVERSATION]: ($state, id) => {
    recentGet = undefined;
    const { [id]: deletedRecord, ...remainingRecords } = $state.records;
    const { [id]: deletedPagination, ...remainingPagination } =
      $state.pagination || {};
    $state.records = remainingRecords;
    $state.pagination = remainingPagination;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
